import { forwardRef, type InputHTMLAttributes, type ReactNode, useId } from 'react';
import { registerEnglishArea } from '@/i18n/catalogue';
import { useT } from '@/i18n/LocaleProvider';
import { common } from '@featit/shared/i18n/messages/en/common';

registerEnglishArea('common', common);

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  /**
   * T-1144: a form where every field is plainly needed draws no star; a screen reader still hears it.
   */
  quietRequired?: boolean;
  /**
   * 44 px tall below the `sm` breakpoint, for forms that are mostly filled in on a phone — the
   * signed-out pages (I-0653). From `sm` up the field keeps the compact desktop height.
   */
  touchTarget?: boolean;
  /**
   * Drawn inside the field at its end, centred on the field's own box whatever the label above it
   * (I-2229: the lock on a fixed address). Decorative: give it `aria-hidden`.
   */
  trailing?: ReactNode;
}

/**
 * I-2491: a disabled field reads as disabled in both themes: outlined instead of filled, its text
 * dimmed and still readable (4.5:1 or more on the page and on a card; it can carry a value such as
 * "The agent's default"). On a card the card's rule draws the line and keeps the card's fill
 * (`index.css`, FE-1). The browser's own `opacity: 0.7` on a disabled select is taken off, so the
 * text is the token. Each rule is a `dark:disabled:` one in dark: a plain `disabled:` loses to
 * `dark:text-white`, which is how a disabled field in dark read as an enabled one
 * (`dialogField.contrast.test.ts`).
 *
 * I-2595: a field refused or held by `gateFieldProps` is `aria-disabled`, not `disabled`, so it keeps
 * its focus and its reason; each rule has an `aria-disabled:` twin so it looks the same. Its hover and
 * focus fills are taken off too, its focus ring stays, and it opts out of the refused field's 0.6
 * fade (`index.css`): the outline and the token already say it is not for changing.
 */
export const DISABLED_FIELD_CLASSES = [
  'disabled:cursor-not-allowed disabled:opacity-100 disabled:bg-transparent disabled:text-gray-600 disabled:ring-1 disabled:ring-inset disabled:ring-gray-300 dark:disabled:bg-transparent dark:disabled:text-gray-400 dark:disabled:ring-gray-600',
  'aria-disabled:cursor-not-allowed aria-disabled:[filter:none] aria-disabled:bg-transparent aria-disabled:hover:bg-transparent aria-disabled:focus:bg-transparent aria-disabled:text-gray-600 aria-disabled:ring-1 aria-disabled:ring-inset aria-disabled:ring-gray-300 aria-disabled:focus:ring-2 aria-disabled:focus:ring-focus',
  'dark:aria-disabled:bg-transparent dark:aria-disabled:hover:bg-transparent dark:aria-disabled:focus:bg-transparent dark:aria-disabled:text-gray-400 dark:aria-disabled:ring-gray-600 dark:aria-disabled:focus:ring-focus',
].join(' ');

/**
 * Shared control chrome so inputs, selects and textareas line up pixel for pixel. Borderless: a light
 * fill that reads on the page. On a grey card the card itself gives every field the canvas fill and a
 * 3:1 line (FE-1, I-1451, I-1452: the card's rule in `index.css`), so no field asks for it.
 */
export const controlClasses = `block w-full rounded-md border-0 bg-gray-100 px-2.5 py-1.5 text-[13px] text-gray-900 shadow-sm transition-colors duration-150 enabled:hover:bg-gray-200 focus:bg-white focus:outline-none focus:ring-2 focus:ring-focus dark:bg-[#303030] dark:text-gray-100 dark:enabled:hover:bg-[#3a3a3a] dark:focus:bg-[#212121] ${DISABLED_FIELD_CLASSES}`;

/**
 * A field that is filled in on a phone: 44 px tall and 16 px text below `sm`, the size under which
 * iOS zooms the whole page into the field on focus. From `sm` up it keeps the desktop's size.
 */
export const PHONE_FIELD_CLASSES = 'max-sm:h-touch-field max-sm:text-base';

/**
 * The dialogs' select and field: *New conversation*, *Start session* and its model field, and *New
 * work area*. Hover lightens an enabled one only. The caller adds its focus ring colour.
 */
export const DIALOG_FIELD_CLASSES = `h-9 w-full rounded-md border-0 bg-gray-100 px-3 text-sm text-gray-900 outline-none transition-colors enabled:hover:bg-gray-200 focus:bg-white focus:ring-2 dark:bg-[#303030] dark:text-white dark:enabled:hover:bg-[#3a3a3a] dark:focus:bg-[#212121] ${DISABLED_FIELD_CLASSES}`;

export const fieldLabelClasses =
  'mb-1.5 block text-[13px] font-medium text-gray-700 dark:text-gray-200';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    id,
    className = '',
    required,
    quietRequired = false,
    touchTarget = false,
    trailing,
    'aria-describedby': describedBy,
    ...props
  },
  ref,
) {
  const t = useT();
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const descriptionIds = [describedBy, hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div>
      {label && (
        <label htmlFor={controlId} className={fieldLabelClasses}>
          {label}
          {required ? (
            <>
              {quietRequired ? null : (
                <span aria-hidden="true" className="ml-1 text-red-600 dark:text-red-400">
                  *
                </span>
              )}
              <span className="sr-only">{t('common.requiredMark')}</span>
            </>
          ) : null}
        </label>
      )}
      {trailing ? (
        <div className="relative">
          <input
            ref={ref}
            id={controlId}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={descriptionIds}
            className={`${controlClasses} ${touchTarget ? 'max-sm:min-h-touch sm:h-auto' : ''} ${
              error ? 'ring-2 ring-red-500/60 focus:ring-red-500/70 dark:ring-red-500/60' : ''
            } pr-8 ${className}`}
            {...props}
          />
          <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
            {trailing}
          </span>
        </div>
      ) : (
        <input
          ref={ref}
          id={controlId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={descriptionIds}
          className={`${controlClasses} ${touchTarget ? 'max-sm:min-h-touch sm:h-auto' : ''} ${
            error ? 'ring-2 ring-red-500/60 focus:ring-red-500/70 dark:ring-red-500/60' : ''
          } ${className}`}
          {...props}
        />
      )}
      {hint && (
        <p id={hintId} className="mt-1.5 text-[12px] text-gray-500">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-1.5 text-[12px] leading-5 text-red-600 dark:text-red-300"
        >
          {error}
        </p>
      )}
    </div>
  );
});
